import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Users, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { upcomingEvents } from '../mock';
import { useTranslation } from '../i18n/LanguageContext';

const HomePage = () => {
  const { t, lang } = useTranslation();
  const featuredEvents = upcomingEvents.filter(event => event.featured);
  const nextEvents = upcomingEvents.slice(0, 3);
  const dateLocale = lang === 'es' ? 'es-ES' : 'en-US';

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative text-white overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/images/holy-fire-bg.jpg')" }}></div>
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-28 md:py-40 relative z-10">
          <div className="max-w-5xl mx-auto text-center space-y-10 fade-in">
            <h1 className="display-hero text-4xl sm:text-5xl md:text-6xl lg:text-7xl flex flex-col leading-[0.95]">
              <span className="block">{t('home.heroLine1')}</span>
              <span className="block italic font-light opacity-90">{t('home.heroLine2')}</span>
              <span className="block">{t('home.heroLine3')}</span>
            </h1>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center pt-6">
              <Link to="/give" className="learn-more text-white">
                {t('home.ctaDonate')}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/events" className="learn-more text-white">
                {t('home.ctaEvents')}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-background to-transparent"></div>
      </section>

      {/* What is NPCMOP Section */}
      <section className="py-24 md:py-32 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="display-hero text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
              <span className="italic font-light">{t('home.whatIs')}</span> <span className="text-gradient">NPCMOP</span>?
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              {t('home.whatIsBody')}
            </p>
            <div className="pt-6">
              <Link to="/about" className="learn-more text-foreground">
                {t('home.learnAboutUs')}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Moments Gallery — Vive-style asymmetric photo grid */}
      <section className="py-24 md:py-32 bg-background border-t">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-6 uppercase tracking-[0.3em] text-xs">
              {t('home.momentsBadge')}
            </Badge>
            <h2 className="display-hero text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
              {t('home.momentsTitle')} <span className="italic font-light">{t('home.momentsTitleAccent')}</span>
            </h2>
          </div>
          <div className="grid grid-cols-12 gap-4 max-w-6xl mx-auto">
            <div className="col-span-12 md:col-span-7 relative overflow-hidden rounded-2xl aspect-[4/3] md:aspect-[16/10] group">
              <img src="/images/moments-prayer.jpg" alt={t('home.moment1Title')} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="col-span-6 md:col-span-5 relative overflow-hidden rounded-2xl aspect-square group">
              <img src="/images/moments-lifted-hands.jpg" alt={t('home.moment2Title')} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="col-span-12 relative overflow-hidden rounded-2xl aspect-[16/9] group">
              <img src="/images/moments-anointed.jpg" alt={t('home.moment3Title')} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <p className="uppercase tracking-[0.25em] text-[10px] mb-2 opacity-90">{t('home.moment3Cat')}</p>
                <h3 className="display-hero text-2xl md:text-3xl">{t('home.moment3Title')} <span className="italic font-light">{t('home.moment3TitleAccent')}</span></h3>
              </div>
            </div>
            <div className="col-span-12 relative overflow-hidden rounded-2xl aspect-[21/9] group">
              <img src="/images/moments-revive.jpg" alt="REVIVE worship night with NPCMOP" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8 text-white">
                <p className="uppercase tracking-[0.3em] text-xs mb-2 opacity-90">{t('home.moment5Cat')}</p>
                <h3 className="display-hero text-2xl md:text-3xl">{t('home.moment5Title')} <span className="italic font-light">{t('home.moment5TitleAccent')}</span></h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Events */}
      {featuredEvents.length > 0 && (
        <section className="py-24 md:py-32 section-gradient">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <Badge variant="default" className="mb-6 uppercase tracking-[0.3em] text-xs">
                {featuredEvents.length > 1 ? t('home.featuredBadge') : t('home.featuredBadgeSingle')}
              </Badge>
              <h2 className="display-hero text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
                <span className="italic font-light">{t('home.dontMissTitle')}</span> {t('home.dontMissAccent')}
              </h2>
            </div>
            <div className={`grid gap-8 max-w-6xl mx-auto ${featuredEvents.length > 1 ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
              {featuredEvents.map((event) => (
                <Card key={event.id} className="overflow-hidden card-hover border-2 flex flex-col">
                  <div className="relative h-56 md:h-64">
                    <img
                      src={event.image}
                      alt={t(event.title)}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-primary text-primary-foreground shadow-lg">
                        {t(event.category)}
                      </Badge>
                    </div>
                  </div>
                  <CardContent className="p-8 flex flex-col flex-1">
                    <CardTitle className="text-2xl md:text-3xl mb-4">
                      {t(event.title)}
                    </CardTitle>
                    <CardDescription className="text-base mb-6">
                      {t(event.description)}
                    </CardDescription>
                    <div className="space-y-3 mb-6 text-sm">
                      <div className="flex items-center space-x-3">
                        <Calendar className="h-5 w-5 text-primary" />
                        <span className="font-medium">
                          {event.time
                            ? t(event.time)
                            : new Date(event.date).toLocaleDateString(dateLocale, {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <Users className="h-5 w-5 text-primary" />
                        <span>{event.location}</span>
                      </div>
                    </div>
                    {event.registrationOpen && event.registrationLinks && (
                      <div className="space-y-3 mt-auto">
                        <Button
                          size="lg"
                          className="btn-hover w-full"
                          onClick={() => window.open(event.registrationLinks.student, '_blank')}
                        >
                          {t('home.hsStudentReg')}
                          <ExternalLink className="ml-2 h-5 w-5" />
                        </Button>
                        <Button
                          size="lg"
                          variant="outline"
                          className="btn-hover w-full"
                          onClick={() => window.open(event.registrationLinks.counselor, '_blank')}
                        >
                          {t('home.hsCounselorReg')}
                          <ExternalLink className="ml-2 h-5 w-5" />
                        </Button>
                      </div>
                    )}
                    {event.registrationOpen && event.registrationLink && (
                      <Button
                        size="lg"
                        className="btn-hover w-full mt-auto"
                        onClick={() => window.open(event.registrationLink, '_blank')}
                      >
                        {t('common.registerNow')}
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Events Grid */}
      {nextEvents.length > 0 && (
        <section className="py-24 md:py-32 bg-background">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:justify-between md:items-end mb-16 gap-6">
              <div>
                <Badge variant="secondary" className="mb-6 uppercase tracking-[0.3em] text-xs">
                  {t('home.whatsNextBadge')}
                </Badge>
                <h2 className="display-hero text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
                  {t('home.upcomingEventsTitle')} <span className="italic font-light">{t('home.upcomingEventsAccent')}</span>
                </h2>
              </div>
              <Link to="/events" className="learn-more text-foreground hidden md:inline-flex">
                {t('common.viewAll')}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {nextEvents.map((event) => (
                <Link to="/events" key={event.id} className="group block">
                  <div className="relative overflow-hidden rounded-2xl aspect-[4/5] card-hover">
                    <img
                      src={event.image}
                      alt={t(event.title)}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
                    <div className="absolute top-5 left-5">
                      <Badge className="bg-white/90 text-gray-900 uppercase tracking-wider text-[10px]">
                        {t(event.category)}
                      </Badge>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-7 text-white">
                      <div className="text-xs uppercase tracking-[0.25em] mb-3 text-white/80 flex items-center">
                        <Calendar className="h-3 w-3 mr-2" />
                        {new Date(event.date).toLocaleDateString(dateLocale, {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </div>
                      <h3 className="display-hero text-2xl md:text-3xl mb-3 leading-tight">
                        {t(event.title)}
                      </h3>
                      <p className="text-sm text-white/80 line-clamp-2 mb-4">
                        {t(event.description)}
                      </p>
                      <span className="learn-more text-white text-xs">
                        {t('common.learnMore')}
                        <ArrowRight className="h-3 w-3" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-10 md:hidden">
              <Link to="/events" className="learn-more text-foreground">
                {t('common.viewAllEvents')}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 md:py-40 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/images/holy-fire-bg.jpg')" }}></div>
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h2 className="display-hero text-3xl sm:text-4xl md:text-5xl lg:text-6xl">
              {t('home.ctaReadyTitle')} <span className="italic font-light">{t('home.ctaReadyAccent')}</span>
            </h2>
            <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
              {t('home.ctaReadyBody')}
            </p>
            <div className="flex flex-col sm:flex-row gap-8 justify-center pt-6">
              <Link to="/get-involved" className="learn-more text-white">
                {t('home.ctaGetInvolved')}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/contact" className="learn-more text-white">
                {t('common.contactUs')}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
